import { type ComponentType, type FC } from "react";
import { useLocation } from "react-router-dom";

import { UnstyledButton } from "@mantine/core";
import { FolderIcon, HelpIcon, type IconProps, TrendingIcon } from "@snapchat/snap-design-system-icons";
import { FormattedMessage, type MessageDescriptor, useIntl } from "react-intl";

import { getPageKey } from "../utils";

import styles from "./AssistantChatSuggestions.module.css";
import { messages } from "./messages.generated";

interface Props {
    onSelect: (prompt: string) => void;
    disabled?: boolean;
}

interface Suggestion {
    message: MessageDescriptor & { defaultMessage: string };
    Icon: ComponentType<IconProps>;
}

const CHIP_ICON_SIZE = 16;

const DEFAULT_SUGGESTIONS: Suggestion[] = [
    { message: messages.suggestDefault1, Icon: FolderIcon },
    { message: messages.suggestDefault2, Icon: HelpIcon },
    { message: messages.suggestDefault3, Icon: TrendingIcon },
];

const SUGGESTIONS: Record<string, Suggestion[]> = {
    insights: [
        { message: messages.suggestInsights1, Icon: TrendingIcon },
        { message: messages.suggestInsights2, Icon: HelpIcon },
        { message: messages.suggestInsights3, Icon: FolderIcon },
    ],
    submit: [
        { message: messages.suggestSubmit1, Icon: HelpIcon },
        { message: messages.suggestSubmit2, Icon: TrendingIcon },
        { message: messages.suggestSubmit3, Icon: FolderIcon },
    ],
    assets: [
        { message: messages.suggestAssets1, Icon: FolderIcon },
        { message: messages.suggestAssets2, Icon: HelpIcon },
        { message: messages.suggestAssets3, Icon: TrendingIcon },
    ],
    lenses: [
        { message: messages.suggestLenses1, Icon: FolderIcon },
        { message: messages.suggestLenses2, Icon: HelpIcon },
        { message: messages.suggestLenses3, Icon: TrendingIcon },
    ],
};

export const AssistantChatSuggestions: FC<Props> = ({ onSelect, disabled = false }) => {
    const intl = useIntl();
    const { pathname } = useLocation();
    const suggestions = SUGGESTIONS[getPageKey(pathname)] ?? DEFAULT_SUGGESTIONS;

    return (
        <div className={styles.empty}>
            <h3 className={styles.greeting}>
                <FormattedMessage {...messages.emptyHeadline} />
            </h3>
            <p className={styles.body}>
                <FormattedMessage {...messages.emptyBody} />
            </p>
            <p className={styles.eyebrow}>
                <FormattedMessage {...messages.suggestedEyebrow} />
            </p>
            <div className={styles.chips}>
                {suggestions.map(({ message, Icon }) => (
                    <UnstyledButton
                        key={message.id}
                        className={styles.chip}
                        disabled={disabled}
                        onClick={() => {
                            onSelect(intl.formatMessage(message));
                        }}
                    >
                        <Icon size={CHIP_ICON_SIZE} className={styles.chipIcon} />
                        <FormattedMessage {...message} />
                    </UnstyledButton>
                ))}
            </div>
        </div>
    );
};
